import { useEffect, useState } from 'react'
import { client } from '../lib/sanity'
import PublicationCard from '../components/PublicationCard'
import FilterSelect from '../components/FilterSelect'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import { motion } from 'framer-motion'

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'published', label: 'Published' },
    { value: 'preprint', label: 'Preprint' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'in_progress', label: 'In Progress' }
]

export default function Publications() {
    const [publications, setPublications] = useState([])
    const [selectedStatus, setSelectedStatus] = useState('')
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(true)

    const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.12 } } }

    useEffect(() => {
        setLoading(true)
        client.fetch(`*[_type == "publication"]|order(featured desc, year desc){
        title,
        authors,
        venue,
        year,
        status,
        abstract,
        paperUrl,
        arxivUrl,
        codeUrl,
        bibtex,
        featured
      }`).then(data => {
            setPublications(data.map(p => ({ ...p, featured: Boolean(p.featured), authors: Array.isArray(p.authors) ? p.authors : [] })))
        }).finally(() => setLoading(false))
    }, [])

    const filteredPublications = publications.filter(p => {
        const matchStatus = !selectedStatus || p.status === selectedStatus
        const q = search.trim().toLowerCase()
        const matchSearch = !q || [
          p.title,
          p.venue,
          p.abstract,
          ...(Array.isArray(p.authors) ? p.authors : [])
        ].filter(Boolean).some(s => s.toLowerCase().includes(q))
        return matchStatus && matchSearch
    })

    return (
        <div className="min-h-screen bg-white dark:bg-zinc-950">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
                    <div className="md:col-span-3">
                        <h1 className="text-4xl font-serif font-bold text-black dark:text-white md:sticky md:top-24">Publications</h1>
                    </div>

                    <div className="md:col-span-9">
                        <div className="flex flex-wrap gap-3 mb-8 items-center">
                            <FilterSelect value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} className="w-auto">
                                {STATUS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FilterSelect>
                            <Input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search publications"
                                className="min-w-[220px] w-auto"
                            />
                            <Button variant="secondary" onClick={() => { setSelectedStatus(''); setSearch('') }}>
                                Reset
                            </Button>
                            <span className="text-sm font-mono text-zinc-600 dark:text-zinc-300 ml-2">{filteredPublications.length} result{filteredPublications.length === 1 ? '' : 's'}</span>
                        </div>

                        {loading && (
                            <div className="grid grid-cols-1 gap-6">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i} className="h-40 border-2 border-black dark:border-white bg-white dark:bg-zinc-950 animate-pulse" />
                                ))}
                            </div>
                        )}
                        {!loading && filteredPublications.length === 0 && (
                            <div className="text-zinc-700 dark:text-zinc-300">No publications match these filters.</div>
                        )}

                        <motion.div
                            className="grid grid-cols-1 gap-6"
                            variants={container}
                            initial="hidden"
                            animate={loading ? 'hidden' : 'show'}
                        >
                            {filteredPublications.map((pub, idx) => (
                                <PublicationCard key={idx} {...pub} />
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    )
}
